import React, { Suspense, lazy } from 'react';
import classnames from 'classnames';
import FormPopup from '@wix/da-shared-react/pkg/Popup/Form';
import IconButton from '@wix/da-shared-react/pkg/Button/IconButton';
import IconClose from '@wix/da-shared-react/pkg/Icons/CloseBold';

import { withTranslation, WithTranslation } from 'react-i18next';
import {
  MAXIMUM_GIF_AVATAR_FILESIZE,
  MAXIMUM_AVATAR_FILESIZE,
  MAXIMUM_GIF_INPUT_FILESIZE,
} from '../constants';
import LoadingIndicator from '@wix/da-shared-react/pkg/LoadingIndicator';
import s from './AvatarEditPopup.scss';

const ImageCrop = lazy(() =>
  import(/* webpackChunkName: "avatarEdit" */ '../ImageCrop')
);

export interface Props {
  isOpen: boolean;
  onRequestClose: () => void;
  uploadAvatar?: ({ file: Blob, filename: string }) => void;
  className?: string;
}

const defaultProps = {
  isOpen: false,
};

export interface State {
  canSubmit: boolean;
  errorMessage?: string;
}

const defaultState = {
  canSubmit: false,
  errorMessage: undefined,
};

class AvatarEditPopup extends React.Component<Props & WithTranslation, State> {
  static defaultProps = defaultProps;
  state = defaultState;
  editorRef: React.RefObject<any>;

  constructor(props) {
    super(props);
    this.editorRef = React.createRef();
  }

  render() {
    const { isOpen, t } = this.props;
    return (
      <FormPopup
        isOpen={isOpen}
        className={s['popup']}
        title={t<string>('widgets.avatarEdit.popupTitle')}
        onSubmit={this.saveAvatar}
        onCancel={this.handleRequestClose}
        onClose={this.handleRequestClose}
        disabledSubmit={!this.state.canSubmit}
        footerExtras={this.renderFooterExtras()}
        contentClassName={s['content']}
        footerClassName={s['footer']}
      >
        <Suspense fallback={<LoadingIndicator />}>
          <ImageCrop
            className={s['editor']}
            ref={this.editorRef}
            onFileDrop={this.handleFileDrop}
            accept="image/jpeg, image/png, image/gif"
            imageQuality={0.92}
          />
        </Suspense>
      </FormPopup>
    );
  }

  renderFooterExtras() {
    if (this.state.errorMessage) {
      return this.renderAlert(this.state.errorMessage, s['alert-error']);
    }
  }

  renderAlert(message, className: null | string = null) {
    return (
      <div className={classnames(s['alert'], className)}>
        <div className={s['alert-message']}>{message}</div>
        <IconButton onClick={this.handleDismissAlert} noHoverEffect>
          <IconClose className={s['alert-close']} />
        </IconButton>
      </div>
    );
  }

  handleDismissAlert = () => {
    this.setState({ errorMessage: undefined });
  };

  saveAvatar = e => {
    if (!this.editorRef.current) {
      return;
    }
    this.setState({ canSubmit: false });
    this.editorRef.current
      .toBlob()
      .then(({ blob: file, filename }) => {
        const { uploadAvatar } = this.props;
        switch (file.type) {
          case 'image/gif':
            if (file.size > MAXIMUM_GIF_AVATAR_FILESIZE) {
              throw new Error(
                'widgets.avatarEdit.error.maximumGifFileSizeExceeded'
              );
            }
            break;
          case 'image/jpeg':
          case 'image/png':
            if (file.size > MAXIMUM_AVATAR_FILESIZE) {
              throw new Error(
                'widgets.avatarEdit.error.maximumFileSizeExceeded'
              );
            }
            break;
          default:
        }

        uploadAvatar &&
          uploadAvatar({
            file,
            filename,
          });
        this.handleRequestClose();
      })
      .catch(err => {
        const { t } = this.props;
        this.setState({
          canSubmit: true,
          errorMessage: err && err.message && t(err.message),
        });
      });
  };

  handleRequestClose = () => {
    // reset the canSubmit state once the popup has been closed
    this.setState({ canSubmit: false, errorMessage: undefined });
    this.props.onRequestClose && this.props.onRequestClose();
  };

  handleFileDrop = file => {
    const { t } = this.props;
    let canSubmit = true;
    let errorMessage = this.state.errorMessage as any;
    if (file.type === 'image/gif' && file.size > MAXIMUM_GIF_INPUT_FILESIZE) {
      canSubmit = false;
      errorMessage = `${t('widgets.avatarEdit.error.cannotProcessFile')} ${t(
        'widgets.avatarEdit.error.trySmallerAnimatedGif'
      )}`;
    }
    this.setState({ canSubmit, errorMessage });
  };
}

export default withTranslation()(AvatarEditPopup);
